import beta from 'images/icon/beta.png';
import * as React from 'react';
import { useHistory } from 'react-router';
import { NavLink } from 'react-router-dom';
import { Collapse } from 'reactstrap';
import { useRecoilState, useRecoilValue } from 'recoil';
import { AAssortments } from '../../atoms/assortment';
import { AColors } from '../../atoms/colors/colors';
import { AFormTemplate } from '../../atoms/forms';
import { AEventTypes } from '../../atoms/global/events';
import { AFieldReportInstallers } from '../../atoms/global/fieldReportInstallers';
import { AExternalMappings, ASelectedExternalMappingId } from '../../atoms/global/mappings';
import { AOrderStatuses } from '../../atoms/global/orders';
import { AProfile, AUsers, isOwner, useMe } from '../../atoms/global/users';
import { AViews } from '../../atoms/global/views';
import { ENV } from '../../config/keys';
import { Policy } from '../../containers_v2/auth/login/model';
import PermissionContext from '../../containers_v2/permissions/PermissionContext';
import { isAllowedToWithPerm } from '../../containers_v2/permissions/PermissionProvider';
import Restricted from '../../containers_v2/permissions/Restricted';
import RestrictedAllowDenyList from '../../containers_v2/permissions/RestrictedAllowDenyList';
import { FlexDiv } from '../../containers_v2/products/style';
import { resetParams } from '../../helpers/localStorage';
import Routes, { Route, SubRoute } from '../../routes/Routes';
import DoodleGroup3, { DoodleGroup3AnimationDiv } from '../../styles/custom-icons/doodles/group-3';
import { Dot } from '../../styles/global/css/Dot';
import { Open } from '../../styles/global/css/Open';
import { BackgroundColor, PrimaryColor } from '../../styles/global/css/Utils';
import { To } from '../../Types';
import { useFunctionState } from '../../utils/customHooks';
import { getUrlParameterByName, isSuperAdmin } from '../utils';
import { Container, DotContainer, Item, LogoContainer, NAV_ITEM_HEIGHT, NavContainer, RouteContainer, SIDEBAR_CLOSE_WIDTH, SidebarItemContainer, SubText, TextItem } from './style';
import { ObjectAction } from './web-types/objectActions';

export const getMainPage = (permissions: Policy, reportsfields: string[] = []): string => Routes[0]
	.routes
	.filter(route => (route.menu || route.submenu) && !route.admin)
	.sort((a, b) => (a.precedence ?? 999) - (b.precedence ?? 999))
	.filter(route => !(route.path == '/orders' && !reportsfields.find(e => e == 'orders')) && isAllowedToWithPerm(route.permission?.map((objectAction: ObjectAction): To => ({ objectAction })) ?? [], permissions, undefined, undefined, [], []))[0]?.path ?? '/';

function isRouteAuthorized(route: SubRoute | Route, isAdmin: boolean, isOwner: boolean, fields: ('orders' | 'shelf_audit')[]): boolean {
	if (route.admin && !isAdmin) return false;
	if (route.owner && !isOwner) return false;
	if (route.path === '/orders' && !fields.some(f => f === 'orders')) return false;
	else if (route.path === '/enform/list' && !fields.some(f => f === 'shelf_audit')) return false;
	return true;
}

export default function Sidebar(): JSX.Element {
	const _aProfile = useRecoilValue(AProfile);
	const _colors = useRecoilValue(AColors);
	const _orderStatuses = useRecoilValue(AOrderStatuses);
	const _users = useRecoilValue(AUsers);
	const _eventTypes = useRecoilValue(AEventTypes);
	const _views = useRecoilValue(AViews);
	const assortments = useRecoilValue(AAssortments);
	const _fieldReportInstallers = useRecoilValue(AFieldReportInstallers);
	const BotMenuList = Routes[0].routes.filter(route => route.menu);
	const TopMenuList = Routes[0].routes.filter(route => route.submenu);
	const [isOpen, setIsOpen] = useFunctionState<boolean>(false, ({ newValue }) => {
		if (!newValue) setLocalIsOpen(undefined);
		setDisplayText(newValue);
		return newValue;
	});
	const externalMappings = useRecoilValue(AExternalMappings);
	const [selectedExternalMappingId, setSelectedExternalMappingId] = useRecoilState(ASelectedExternalMappingId);
	React.useEffect(() => {
		if (selectedExternalMappingId.company) return;
		const companyMapping = externalMappings.find(em => em.mapping_type_id == 1);
		if (companyMapping) setSelectedExternalMappingId({ company: companyMapping.id });
	}, [selectedExternalMappingId.company, externalMappings]);

	const [localTimeout, setLocalTimeout] = React.useState<NodeJS.Timeout>();
	const fieldReportInstallers = useRecoilValue(AFieldReportInstallers);
	const reportsfields = fieldReportInstallers.map(fri => fri.name);
	// animation
	const [displayText, setDisplayText] = React.useState<boolean>(false);
	const [animateLogo, setAnimateLogo] = React.useState<number>(0);
	const permissions = React.useContext(PermissionContext).getPermissions();
	const isAdmin = isSuperAdmin();
	const me = useMe();
	const meOwner = isOwner(me) || isAdmin;
	const [localIsOpen, setLocalIsOpen] = React.useState<string>();
	const templates = useRecoilValue(AFormTemplate).instanceForms;
	const formId = parseInt(getUrlParameterByName('id') ?? 'NaN');
	const history = useHistory();


	const mainPage = getMainPage(permissions, reportsfields);

	function resetStorageForProductsAndChangeHash(route: SubRoute | Route, e) {
		resetParams();
		// If the route is the same as the current path and the hash is not included in the route's hash list, prevent the default action and change the hash
		if (history.location.pathname === route.path && route.hashs !== undefined && !route.hashs.includes(history.location.hash)) {
			e.preventDefault();
			window.location.hash = route.hashs?.[0];
		}

	}

	function isSubRouteActive(subroute: SubRoute): boolean {
		if (subroute.hashs?.includes(history.location.hash) === false) {
			return false;
		}
		return history.location.pathname == subroute.path;
	}

	function isRouteActive(route: Route): boolean {
		if (route.hashs?.includes(history.location.hash) === false) {
			return false;
		}
		return history.location.pathname == route.path || (route.subroutes?.some(sub => isSubRouteActive(sub)) ?? false);
	}

	function createSubMenu(route: Route, index: number): JSX.Element {
		const [isActive, setIsActive] = React.useState<boolean>(isRouteActive(route));
		const [isHover, setIsHover] = React.useState<boolean[]>([false]);

		React.useEffect(() => {
			setIsActive(isRouteActive(route));
		}, [history.location]);
		const subRouteLength = route.subroutes?.length ?? 0;

		return <Restricted to={route.permission?.map((objectAction: ObjectAction): To => ({ objectAction })) ?? []} key={`Route[${route.path}]`}>
			<SidebarItemContainer
				index={index}
				key={route.path}
				hidden={!isRouteAuthorized(route, isAdmin, meOwner, reportsfields)}
			>
				{(route.subroutes == null || subRouteLength == 0) && route.sectionKey !== 'forms'
					? <NavLink
						key={`route[${route.path}]`}
						to={{
							pathname: route.path,
							hash: route.hashs?.[0],
						}}
						onClick={(e) => resetStorageForProductsAndChangeHash(route, e)}
						onMouseEnter={() => {
							isHover[0] = true;
							setIsHover([...isHover]);
						}}
						onMouseLeave={() => {
							isHover[0] = false;
							setIsHover([...isHover]);
						}}
						style={{ height: NAV_ITEM_HEIGHT, display: 'inline-block', width: !isOpen ? '100%' : '80%', overflow: 'hidden', minWidth: SIDEBAR_CLOSE_WIDTH }}
					>
						<LogoContainer>
							<Item isActive={isActive}>
								{isActive ? route.activeImg : isHover[0] ? route.hoverImg : route.logoImg}
							</Item>
						</LogoContainer>
						<TextItem isActive={!!(isActive || isHover[0])} isOpen={isOpen && displayText} weight>{route.name}{route.beta && <img src={beta} height={15} style={{ marginLeft: 10 }} />}</TextItem>

					</NavLink>
					: <NavContainer
						onClick={() => setLocalIsOpen(localIsOpen => localIsOpen === route.path ? undefined : route.path)}
						onMouseEnter={() => {
							isHover[0] = true;
							setIsHover([...isHover]);
						}}
						onMouseLeave={() => {
							isHover[0] = false;
							setIsHover([...isHover]);
						}}
						isOpen={isOpen}
					>
						<LogoContainer>
							<Item isActive={isActive}>
								{isActive ? route.activeImg : isHover[0] ? route.hoverImg : route.logoImg}
							</Item>
						</LogoContainer>
						<TextItem isActive={!!(isActive || isHover[0])} isOpen={isOpen && displayText} weight>{route.name}</TextItem>
					</NavContainer>
				}
				{subRouteLength > 0 &&
					<div>
						<Open
							size={11}
							isOpen={localIsOpen === route.path}
							onClick={() => {
								setLocalIsOpen(localIsOpen => localIsOpen === route.path ? undefined : route.path);
							}}
							hidden={!(isOpen && displayText)}
						/>
					</div>
				}
			</SidebarItemContainer>
			<Collapse isOpen={localIsOpen === route.path && displayText && isOpen}>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
					{route.subroutes?.map((subroute, i) => {
						// only display old assortments if there is some assortments
						if (subroute.path == '/assortments-v2' && !isAdmin) {
							if (assortments.length == 0) return;
						}

						const isSubActive = isSubRouteActive(subroute);
						if (subroute.admin && !isAdmin) return undefined;
						if (subroute.owner && !meOwner && !isAdmin) return undefined;
						return (
							(<Restricted cumulative={subroute.cumulative} to={subroute.permission?.map((objectAction: ObjectAction): To => ({ objectAction })) ?? []} key={`SubRoute[${route.path}][${i}]`}>
								<NavLink
									key={`subRoute[${route.path}][${i}]`}
									to={{
										pathname: subroute.path,
										hash: subroute.hashs?.[0],
									}}
									onClick={(e) => resetStorageForProductsAndChangeHash(subroute, e)}
									style={{
										height: 20,
										display: 'block',
										overflow: 'hidden'
									}}
									onMouseEnter={() => {
										isHover[i + 1] = true;
										setIsHover([...isHover]);
									}}
									onMouseLeave={() => {
										isHover[i + 1] = false;
										setIsHover([...isHover]);
									}}
								>
									<FlexDiv padding='0 15px 0 0'>
										<DotContainer><Dot color={isSubActive ? PrimaryColor : BackgroundColor} size='6px' /></DotContainer>
										<SubText isActive={!!(isSubActive || isHover[i + 1])} isOpen={isOpen}>{subroute.name}</SubText>
										{subroute.beta && <img src={beta} height={15} style={{ marginLeft: 10 }} />}
									</FlexDiv>
								</NavLink>
							</Restricted>)
						);
					})}
					{route.sectionKey === 'forms' && [...templates]
						.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
						.map((template, i) => {
							const index = i + 1 + subRouteLength;
							const isActive = history.location.pathname == '/form-instance-list' && formId == template.id && (history.location.hash == '#list' || history.location.hash == 'hash');
							return (
								<Restricted to={{ objectAction: 'ViewForm' }} key={`Form[${route.path}][${i}]`}>
									<RestrictedAllowDenyList target='ReadForm' target_id={template.id}>
										<NavLink
											to={`/form-instance-list?id=${template.id}#list`}
											style={{
												minHeight: 20,
												display: 'block',
												overflow: 'hidden'
											}}
											onMouseEnter={() => {
												isHover[index] = true;
												setIsHover([...isHover]);
											}}
											onMouseLeave={() => {
												isHover[index] = false;
												setIsHover([...isHover]);
											}}
										>
											<FlexDiv padding='0 15px 0 0'>
												<DotContainer><Dot color={isActive ? PrimaryColor : BackgroundColor} size='8px' /></DotContainer>
												<SubText textAlign='left' isActive={!!(isActive || isHover[index])} isOpen={isOpen}>{template.name}</SubText>
											</FlexDiv>
										</NavLink>
									</RestrictedAllowDenyList>
								</Restricted>
							);
						})}
				</div>
			</Collapse>
		</Restricted >;
	}

	return <Container isOpen={isOpen}>
		<RouteContainer
			isOpen={isOpen}
			onMouseLeave={_ => setIsOpen(false)}
			onTransitionEnd={e => {
				if (e.propertyName !== 'width') return;
				setIsOpen(e.currentTarget.matches(':hover'));
			}}
		>
			<SidebarItemContainer index={0}>
				<LogoContainer
					onMouseEnter={() => {
						setLocalTimeout(setTimeout(() => {
							if (animateLogo % 4 == 0) { setAnimateLogo(1); }
						}, 2000));
					}}
					onMouseLeave={() => localTimeout && clearTimeout(localTimeout)}
					onAnimationEnd={() => setAnimateLogo(animateLogo + 1)}
				>
					<NavLink
						key={'route-logo'}
						to={mainPage}
					>
						<DoodleGroup3AnimationDiv>
							<DoodleGroup3 width={SIDEBAR_CLOSE_WIDTH - 4} style={{ marginLeft: -6 }} height={'unset'} doodleColorLeft={ENV === 'edge' ? 'blue' : undefined} doodleColorCenter={ENV === 'edge' ? 'white' : undefined} doodleColorRight={ENV === 'edge' ? 'red' : undefined} />
						</DoodleGroup3AnimationDiv>
					</NavLink>
				</LogoContainer>
			</SidebarItemContainer>
			{TopMenuList.map((r, i) => createSubMenu(r, i + 1))}
			<li className="nav-item-separator" style={{ height: 0, margin: 0, overflow: 'hidden' }} />
			{BotMenuList.map((r, i) => createSubMenu(r, i + 1 + TopMenuList.length))}
		</RouteContainer>
	</Container>;
}
